import { useTranslation } from '../presentation/common/useTranslation.js';
import { presentationText } from '../presentation/text';
import { useEffect, useState, useRef, useCallback } from 'react';

import { QRCodeSVG } from 'qrcode.react';
import { Camera, UserRound } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { getAdventurerQrPass } from '../services/api/partner';
import { getFullImageUrl } from '../services/api/client';
import HeroRank from './HeroRank';
import HeroClass from './HeroClass';
import AdventurerQrPass from './AdventurerQrPass';
import { getKarmaTier } from './KarmaBarometer';
import { karmaIconColor } from '../utils/karmaIconColor';
import KarmaScoreLink from './KarmaScoreLink';
import { categoryTitle, parseCategoryTitle } from '../utils/categoryMilestones';
import './HeroBusinessCard.css';

export default function HeroBusinessCard({ profile, onAvatarClick, uploading, variant = 'default', approvedActionsCount, karmaLinkTo }) {
  const { t, i18n } = useTranslation();

  const [pass, setPass] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [failed, setFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [failedAvatar, setFailedAvatar] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fetchingRef = useRef(false);
  const lastFetchRef = useRef(0);

  const karmaScore = profile.karmaScore ?? profile.karma ?? 0;
  const karmaTier = getKarmaTier(karmaScore, profile.isBanned, profile.karmaSuspendedUntil, profile.karmaRules);
  const karmaTitle = profile.karmaTitle || (karmaTier?.labelKey ? t(karmaTier.labelKey) : '');
  const selectedTitle = parseCategoryTitle(profile.selectedCategoryTitle);
  const selectedTitleLabel = selectedTitle ? categoryTitle(selectedTitle.categoryKey, selectedTitle.level, i18n.language) : '';
  const approvedActions = approvedActionsCount ?? profile.approvedHeroicActionsCount ?? profile.heroicActionsCompleted ?? profile.actionsCount ?? 0;

  const fetchPass = useCallback(async () => {
    if (fetchingRef.current) return;
    if (Date.now() - lastFetchRef.current < 3000) return;
    fetchingRef.current = true;
    lastFetchRef.current = Date.now();
    try {
      const requestedAt = Date.now();
      const data = await getAdventurerQrPass();
      const expiresAt = new Date(data?.expiresAt).getTime();
      const issuedAt = new Date(data?.issuedAt).getTime();
      const duration = (expiresAt && issuedAt && expiresAt > issuedAt) ? (expiresAt - issuedAt) : 60000;
      const expiry = requestedAt + duration - 1000;
      if (!data?.qrToken) throw new Error('Invalid pass');
      setPass({ ...data, localExpiry: expiry });
      setFailed(false);
      setErrorMessage('');
    } catch (error) {
      setFailed(true);
      setErrorMessage(error?.message || '');
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  const { user: currentUser } = useCurrentUser();
  const profileId = profile.userId || profile.id;
  const isOwnProfile = !profileId || currentUser?.id === profileId || currentUser?.userId === profileId;
  const isSlim = variant === 'slim';
  const showQr = isOwnProfile && variant !== 'slim' && variant !== 'no-qr';

  useEffect(() => {
    if (!showQr) return;
    fetchPass();
  }, [attempt, fetchPass, showQr]);

  useEffect(() => {
    if (!showQr) return;
    const clock = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (!pass || pass.localExpiry - current <= 5000) {
        fetchPass();
      }
    }, 1000);
    return () => window.clearInterval(clock);
  }, [pass, fetchPass, showQr]);
  const valid = pass && pass.localExpiry > now;
  
  return <section className={`hero-business-card ${isSlim ? 'hero-business-card-slim' : ''} ${!showQr && !isSlim ? 'hero-business-card-no-qr' : ''}`} aria-label={t('hero_card.title')}>
    <div className="hero-business-main-content">
      
      {/* Top: Avatar & Name */}
      <div className="hbc-identity-row">
        <div className="hbc-cell-avatar">
          <button type="button" className="hero-business-avatar" onClick={onAvatarClick} disabled={uploading || !onAvatarClick} aria-label={t('dashboard_v2.change_avatar')}>
            {profile.avatarUrl && failedAvatar !== profile.avatarUrl ? <img src={getFullImageUrl(profile.avatarUrl)} alt="" onError={() => setFailedAvatar(profile.avatarUrl)} /> : <UserRound aria-hidden="true" />}
            {onAvatarClick && <Camera className="hero-business-camera" aria-hidden="true" />}
          </button>
        </div>
        
        <div className="hbc-cell-name">
          <small>{t('hero_card.title')}</small>
          <h1 title={profile.username}>{profile.username || presentationText('systemCopy.b5451aaa13ba')}</h1>
          {(karmaTitle || selectedTitleLabel) && <div className="hero-business-titles">
            {karmaTitle && <span className="hero-business-karma" style={karmaScore < 0 ? { color: karmaIconColor(karmaScore, profile.karmaRules?.minimum) } : undefined}>{karmaTitle}</span>}
            {karmaTitle && selectedTitleLabel && <span className="hero-business-title-separator" aria-hidden="true">/</span>}
            {selectedTitleLabel && <span className="hero-business-selected-title">{selectedTitleLabel}</span>}
          </div>}
        </div>
      </div>

      {/* Middle: Rank & All Stats in One Row */}
      <div className="hbc-details-row">
        <div className="hbc-cell-class-icon">
          <small className="hbc-rank-label">{t('dashboard_v2.rank', 'Rang')}</small>
          <HeroRank rank={profile.rank} showLabel={false} className="hbc-main-rank-icon" />
        </div>

        <div className="hbc-stats-grid">
          <div className="hbc-stat-group">
            <small>{t('dashboard_v2.hero_class', 'HELDENKLASSE')}</small>
            <div className="hbc-stat-val hbc-class-val">
              <strong><HeroClass heroClass={profile.heroClass} variant="inline" /></strong>
            </div>
          </div>
          <div className="hbc-stat-group">
            <small>{t('hero_card.level', 'Level')}</small>
            <div className="hbc-stat-val hbc-level-val">
              <strong>{profile.level ?? 1}</strong>
            </div>
          </div>
          <div className="hbc-stat-group">
            <small>{t('hero_card.karma', 'Karma')}</small>
            <div className="hbc-stat-val hbc-karma-val">
              <strong style={karmaScore < 0 ? { color: karmaIconColor(karmaScore, profile.karmaRules?.minimum) } : undefined}><KarmaScoreLink to={karmaLinkTo || (isOwnProfile ? '/profile#karma-barometer' : `/adventurers/${profileId}#karma-barometer`)}>{karmaScore >= 0 ? `+${karmaScore}` : karmaScore}</KarmaScoreLink></strong>
            </div>
          </div>
          <div className="hbc-stat-group">
            <small>{t('hero_card.actions', 'Gute Taten')}</small>
            <div className="hbc-stat-val hbc-actions-val">
              <strong>{approvedActions}</strong>
            </div>
          </div>
          <div className="hbc-stat-group">
            <small>{t('hero_card.guild', 'Gilde')}</small>
            <div className="hbc-stat-val hbc-guild-val">
              {profile.guildName ? (
                <span className="hbc-guild-name" title={profile.guildName}>{profile.guildName}</span>
              ) : (
                <span className="hbc-no-guild">-</span>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
    
    {!showQr ? null : (
      <div className="hero-business-pass" onClick={() => setIsModalOpen(true)} role="button" tabIndex={0} title={t('qr_pass.click_to_enlarge', 'Klicken zum Vergrößern')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsModalOpen(true); }}>
        <div className="hero-business-qr">
          {valid ? <QRCodeSVG value={pass.qrToken} size={56} marginSize={1} level="M" bgColor="#ffffff" fgColor="#09090b" title={t('hero_card.qr')} /> : <span role="status">{failed ? errorMessage || t('hero_card.unavailable') : t('hero_card.loading')}</span>}
        </div>
        <div className="hero-business-pass-info">
          <div className="hbc-pass-header">
            <strong>{t('hero_card.qr', 'Aktueller Heldenpass')}</strong>
            {failed ? <button type="button" onClick={(e) => { e.stopPropagation(); setFailed(false); setAttempt(value => value + 1); }}>{t('hero_card.retry')}</button> : <></>}
          </div>
          <span className="hbc-pass-hint">{t('qr_pass.click_to_enlarge', 'Klicken zum Vergrößern')}</span>
        </div>
      </div>
    )}
    {isModalOpen && showQr && (
      <AdventurerQrPass
        username={profile.username}
        heroClass={profile.heroClass}
        rank={profile.rank}
        avatarUrl={profile.avatarUrl ? getFullImageUrl(profile.avatarUrl) : null}
        karmaScore={profile.karmaScore ?? profile.karma ?? 0}
        karmaMinimum={profile.karmaRules?.minimum}
        approvedActions={profile.approvedHeroicActionsCount ?? profile.heroicActionsCompleted ?? 0}
        karmaTitle={karmaTitle}
        selectedCategoryTitle={profile.selectedCategoryTitle}
        hideInline
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    )}
  </section>;
}
